<script setup lang="ts">
	import {
		CategoryScale,
		Chart as ChartJS,
		Legend,
		LineElement,
		LinearScale,
		PointElement,
		Title,
		Tooltip,
	} from 'chart.js'
	import { computed } from 'vue'
	import { Line } from 'vue-chartjs'

	import useCompanyReportHotelTrends from '@/modules/companies/composables/useCompanyReportHotelTrends'

	interface Props {
		companyId?: number
		dateRange?: string[] | null
		creationDateRange?: string[] | null
		statusFilter?: string
		groupByFilter?: string
		searchTravelerId?: number | null
		searchHotelId?: number | null
		createdAt?: string[] | null
		orderBy?: string
		orderDirection?: 'asc' | 'desc'
	}

	const props = defineProps<Props>()

	// Hacer petición autónoma
	const { trends, isFetching: isLoading } = useCompanyReportHotelTrends(
		computed(() => ({
			companyId: props.companyId,
			filters: {
				dates: props.dateRange,
				creationDates: props.creationDateRange,
				status: props.statusFilter !== 'all' ? props.statusFilter : undefined,
				groupBy: props.groupByFilter,
				travelerId: props.searchTravelerId || undefined,
				hotelId: props.searchHotelId || undefined,
				createdAt: props.createdAt || undefined,
				orderBy: props.orderBy || undefined,
				orderDirection: props.orderDirection || undefined,
			},
		}))
	)

	ChartJS.register(
		Title,
		Tooltip,
		Legend,
		CategoryScale,
		LinearScale,
		PointElement,
		LineElement
	)

	const chartData = computed(() => {
		if (!trends.value || trends.value.length === 0) return null

		const formatMonth = (monthStr: string) => {
			const date = new Date(monthStr + '-01')
			return date.toLocaleDateString('es-ES', {
				month: 'long',
				year: 'numeric',
				timeZone: 'UTC',
			})
		}

		const generateWeeks = (monthStr: string) => {
			const [year, month] = monthStr.split('-')
			const firstDate = new Date(Number(year), Number(month) - 1, 1)
			const lastDate = new Date(Number(year), Number(month), 0)

			const weeks: Array<{ start: Date; end: Date; label: string }> = []
			let currentDate = new Date(firstDate)

			while (currentDate <= lastDate) {
				const weekStart = new Date(currentDate)
				const weekEnd = new Date(currentDate)
				weekEnd.setDate(weekEnd.getDate() + 6)

				if (weekEnd > lastDate) {
					weekEnd.setDate(lastDate.getDate())
				}

				const weekStartFormatted = new Intl.DateTimeFormat('es-ES', {
					day: '2-digit',
					month: 'short',
				}).format(weekStart)
				const weekEndFormatted = new Intl.DateTimeFormat('es-ES', {
					day: '2-digit',
					month: 'short',
				}).format(weekEnd)

				weeks.push({
					start: weekStart,
					end: weekEnd,
					label: `${weekStartFormatted} - ${weekEndFormatted}`,
				})
				currentDate.setDate(currentDate.getDate() + 7)
			}

			return weeks
		}

		const isSingleMonth = trends.value.length === 1

		let labels: string[] = []
		let countData: number[] = []
		let spendData: number[] = []

		if (isSingleMonth) {
			const weeks = generateWeeks(trends.value[0].period).sort(
				(a, b) => a.start.getTime() - b.start.getTime()
			)
			labels = weeks.map((w) => w.label)

			const monthCount = trends.value[0].reservations
			const monthSpend = trends.value[0].totalSpend
			const weeksCount = weeks.length

			const countPerWeek = Math.floor(monthCount / weeksCount)
			const spendPerWeek = Math.floor(monthSpend / weeksCount)

			for (let i = 0; i < weeksCount; i++) {
				if (i === weeksCount - 1) {
					countData.push(monthCount - countPerWeek * (weeksCount - 1))
					spendData.push(monthSpend - spendPerWeek * (weeksCount - 1))
				} else {
					countData.push(countPerWeek)
					spendData.push(spendPerWeek)
				}
			}
		} else {
			const mothsSorted = [...trends.value].sort(
				(a, b) => new Date(a.month).getTime() - new Date(b.month).getTime()
			)

			labels = mothsSorted.map((t) => formatMonth(t.period))
			countData = trends.value.map((t) => t.reservations)
			spendData = trends.value.map((t) => t.totalSpend)
		}

		return {
			labels,
			datasets: [
				{
					label: 'Cantidad de Reservas',
					data: countData,
					borderColor: 'rgb(59, 130, 246)',
					backgroundColor: 'rgba(59, 130, 246, 0.1)',
					tension: 0.4,
					yAxisID: 'y',
				},
				{
					label: 'Gasto Total (MXN)',
					data: spendData,
					borderColor: 'rgb(16, 185, 129)',
					backgroundColor: 'rgba(16, 185, 129, 0.1)',
					tension: 0.4,
					yAxisID: 'y1',
				},
			],
		}
	})

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		interaction: {
			mode: 'index' as const,
			intersect: false,
		},
		plugins: {
			legend: {
				position: 'top' as const,
			},
			title: {
				display: true,
				text: 'Tendencias de Reservas y Gastos',
			},
			datalabels: {
				display: false,
			},
			tooltip: {
				callbacks: {
					label: (context: any) => {
						const label = context.dataset.label || ''
						const value = context.parsed.y

						if (label.includes('Gasto')) {
							return `${label}: ${new Intl.NumberFormat('es-MX', {
								style: 'currency',
								currency: 'MXN',
								maximumFractionDigits: 0,
							}).format(value)}`
						}
						return `${label}: ${value}`
					},
				},
			},
		},
		scales: {
			y: {
				type: 'linear' as const,
				display: true,
				position: 'left' as const,
				title: {
					display: true,
					text: 'Cantidad de Reservas',
				},
				beginAtZero: true,
			},
			y1: {
				type: 'linear' as const,
				display: true,
				position: 'right' as const,
				title: {
					display: true,
					text: 'Gasto Total (MXN)',
				},
				beginAtZero: true,
				grid: {
					drawOnChartArea: false,
				},
				ticks: {
					callback: (value: string | number) => {
						return `$${new Intl.NumberFormat('es-MX', {
							maximumFractionDigits: 0,
						}).format(Number(value))}`
					},
				},
			},
		},
	}

	// Exponer isLoading para el componente padre
	defineExpose({
		isLoading,
	})
</script>

<template>
	<div class="overflow-hidden rounded-lg bg-white p-6 shadow">
		<div
			v-if="isLoading && !chartData"
			class="flex items-center justify-center"
			style="height: 300px"
		>
			<div class="text-center">
				<span
					class="material-symbols-outlined mb-2 animate-spin text-4xl text-blue-ef-500"
				>
					progress_activity
				</span>
				<p class="text-sm text-gray-600">Cargando tendencias...</p>
			</div>
		</div>
		<div
			v-else-if="chartData"
			style="height: 300px"
		>
			<Line
				:data="chartData"
				:options="chartOptions"
			/>
		</div>
		<div
			v-else
			class="flex items-center justify-center"
			style="height: 300px"
		>
			<div class="text-center">
				<span class="material-symbols-outlined mb-4 text-6xl text-gray-300">
					query_stats
				</span>
				<p class="text-base font-medium text-gray-600">
					No hay datos disponibles para esta sección
				</p>
				<p class="mt-2 text-sm text-gray-500">
					No se encontraron tendencias de reservas en el período seleccionado
				</p>
			</div>
		</div>
	</div>
</template>
